import { assessSafetyAction } from './safety';
import type { FoodTestId, ProcedureAction, ProcedureState, ProcedureValidation } from './types';

const requiredSequences: Record<FoodTestId, ProcedureAction[][]> = {
  'reducing-sugar': [['add-sample', 'add-benedicts', 'heat-water-bath', 'wait-five-minutes']],
  'non-reducing-sugar': [[
    'add-sample', 'add-dilute-hcl', 'heat-water-bath', 'wait-five-minutes',
    'neutralise-nahco3', 'check-neutral-ph', 'add-benedicts', 'heat-water-bath', 'wait-five-minutes',
  ]],
  starch: [['add-sample', 'add-iodine']],
  protein: [['add-sample', 'add-sodium-hydroxide', 'add-copper-sulfate']],
  lipid: [['add-sample', 'add-ethanol', 'shake', 'add-water']],
  'vitamin-c': [['add-dcpip', 'add-food-drop', 'shake']],
};

function orderedSequenceIsPresent(actions: ProcedureAction[], sequence: ProcedureAction[]): boolean {
  let previousIndex = -1;
  for (const action of sequence) {
    let nextIndex = -1;
    for (let index = previousIndex + 1; index < actions.length; index += 1) {
      if (actions[index] === action) {
        nextIndex = index;
        break;
      }
    }
    if (nextIndex === -1) return false;
    previousIndex = nextIndex;
  }
  return true;
}

/** Validates required actions, order, heating conditions, volumes, and unsafe actions. */
export function validateProcedure(test: FoodTestId, procedure: ProcedureState): ProcedureValidation {
  const actions = procedure.steps.map((step) => step.action);
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!orderedSequenceIsPresent(actions, requiredSequences[test][0])) {
    const messages: Record<FoodTestId, string> = {
      'reducing-sugar': 'Add the sample and Benedict’s solution, then heat in a water bath for five minutes.',
      'non-reducing-sugar': 'Hydrolyse with dilute HCl in a water bath, neutralise and check pH, then add Benedict’s and reheat.',
      starch: 'Add iodine solution to the food sample at room temperature.',
      protein: 'Add sodium hydroxide first, then add copper(II) sulfate.',
      lipid: 'Add ethanol to the sample and shake before adding water.',
      'vitamin-c': 'Add DCPIP first, then add the food sample dropwise and shake.',
    };
    errors.push(messages[test]);
  }

  if (actions.includes('heat-direct-flame')) errors.push(assessSafetyAction('heat-direct-flame').warnings[0]);
  if (actions.includes('heat-sealed-tube') || (procedure.sampleSealed && actions.includes('heat-water-bath'))) {
    errors.push(assessSafetyAction('heat-sealed-tube').warnings[0]);
  }
  if (actions.includes('boil-dry')) errors.push(assessSafetyAction('boil-dry').warnings[0]);
  if (actions.includes('move-hot-tube') && !actions.includes('use-tube-holder')) {
    errors.push(assessSafetyAction('move-hot-tube').warnings[0]);
  }
  if (actions.includes('add-sodium-hydroxide')) warnings.push(assessSafetyAction('add-sodium-hydroxide').warnings[0]);
  if (actions.includes('add-copper-sulfate')) warnings.push(assessSafetyAction('add-copper-sulfate').warnings[0]);
  if (test === 'lipid' && actions.includes('add-ethanol') && actions.includes('heat-water-bath')) {
    warnings.push(assessSafetyAction('use-ethanol-near-flame').warnings[0]);
  }

  if (test === 'reducing-sugar' || test === 'non-reducing-sugar') {
    if (procedure.waterBathTempC !== undefined && (procedure.waterBathTempC < 90 || procedure.waterBathTempC > 100)) {
      errors.push('The water bath is outside the required near-boiling range (90–100 °C). Adjust it before heating the Benedict’s mixture.');
    }
    if (procedure.sampleVolumeMl !== undefined && procedure.reagentVolumesMl?.benedicts !== undefined) {
      const reagentVolume = procedure.reagentVolumesMl.benedicts;
      if (procedure.sampleVolumeMl <= 0 || reagentVolume <= 0) {
        errors.push('Use measurable, non-zero sample and Benedict’s solution volumes.');
      } else {
        const ratio = reagentVolume / procedure.sampleVolumeMl;
        if (ratio < 0.8 || ratio > 1.2) warnings.push('Use approximately equal volumes of food sample and Benedict’s solution for a comparable colour ladder.');
      }
    }
  }

  if (test === 'non-reducing-sugar' && actions.includes('check-neutral-ph')) {
    if (procedure.pH === undefined) {
      errors.push('Record the pH-paper reading; the hydrolysed sample must be confirmed neutral before Benedict’s solution.');
    } else if (procedure.pH < 6 || procedure.pH > 8) {
      errors.push('The hydrolysed sample is not neutral. Add sodium hydrogen carbonate carefully and check pH again before Benedict’s solution.');
    }
  }
  if (test === 'vitamin-c' && procedure.dcpipDrops !== undefined && procedure.dcpipDrops < 1) {
    errors.push('Add at least one measured drop of blue DCPIP before adding the food sample.');
  }

  return { valid: errors.length === 0, errors, warnings };
}